// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
// eslint-disable-next-line import/no-extraneous-dependencies
import Big from 'big.js';

import { getBogoIndividualDiscount, sortSpecials, isCumulative } from 'shared/order/utilities';

const getRewardsToApply = (applicableBogoRewards, applyStowedDiscounts, stowedDiscounts) => {
  if (!applyStowedDiscounts) {
    return _.reject(applicableBogoRewards, ({ isSecpoDtc }) => isSecpoDtc);
  }
  return _.reduce(
    stowedDiscounts,
    (bogoDiscounts, discount) => {
      if (discount.type === 'bogo') {
        bogoDiscounts.push({ ...discount.discountData, _quantityToApply: discount.quantityToApply || 0 });
      }
      return bogoDiscounts;
    },
    []
  );
};

const calculateBogoDiscounts = ({
  applicableBogoRewards,
  applyStowedDiscounts,
  bogoRewardsApplied = {},
  compositePrice,
  credit, // used by applyTaxPreDiscount
  detail,
  dtcIndividualPrice,
  discountTaxOrder,
  originalCompositePrice,
  productTotal,
  receiptDetail,
  globalSpecialsSettings,
  stowDiscounts,
  stowedDiscounts,
}) => {
  const { quantity } = detail;
  let rewardsToApply = getRewardsToApply(applicableBogoRewards, applyStowedDiscounts, stowedDiscounts);
  rewardsToApply = sortSpecials(rewardsToApply, {});
  if (rewardsToApply.length > 0) {
    const bogoSavings = { individual: [], total: Big(0) };

    _.forEach(rewardsToApply, (bogoReward) => {
      let individualPrice;
      if (dtcIndividualPrice) {
        individualPrice = dtcIndividualPrice;
      } else {
        const stackingBehaviorIsCumulative = isCumulative(
          bogoReward.stackingBehavior,
          globalSpecialsSettings.stackingBehavior
        );
        const startingPrice = stackingBehaviorIsCumulative ? originalCompositePrice : compositePrice;
        individualPrice = startingPrice.div(detail.quantity);
      }
      const individualDiscount = getBogoIndividualDiscount(bogoReward, individualPrice);

      let applicableQuantity;
      if (bogoReward?.isDiscountToCartReward) {
        applicableQuantity = quantity;
      } else if (!_.isNil(bogoReward?._quantityToApply)) {
        applicableQuantity = bogoReward._quantityToApply;
      } else {
        applicableQuantity = bogoReward?.applicableQuantity || 0;
      }
      const additionalCredit = individualDiscount.times(applicableQuantity);
      if (stowDiscounts) {
        stowedDiscounts.push({
          applicableQuantity,
          conditionQuantity: bogoReward?.conditionQuantity || 0,
          discountData: bogoReward,
          individualDiscount,
          key: `${bogoReward.specialId}_${bogoReward.rewardId}`,
          type: `bogo`,
        });
      } else {
        bogoRewardsApplied[`rid_${bogoReward.rewardId}`] =
          (bogoRewardsApplied[`rid_${bogoReward.rewardId}`] || 0) + applicableQuantity;
        bogoRewardsApplied[`sid_${bogoReward.specialId}`] =
          (bogoRewardsApplied[`sid_${bogoReward.specialId}`] || 0) + applicableQuantity;
        bogoSavings.individual.push({
          conditionQuantity: bogoReward?.conditionQuantity || 0,
          discountAmount: individualDiscount,
          ...(bogoReward?.dollarDiscount ? { dollarDiscount: bogoReward.dollarDiscount || 0 } : {}),
          ...(bogoReward?.percentDiscount ? { percentDiscount: bogoReward.percentDiscount || 0 } : {}),
          ...(bogoReward?.targetPrice ? { targetPrice: bogoReward.targetPrice || 0 } : {}),
          isDiscountToCartReward: bogoReward?.isDiscountToCartReward,
          maxApplicable: applicableQuantity,
          specialId: bogoReward.specialId,
          stackingBehavior: bogoReward.stackingBehavior,
        });
        bogoSavings.total = bogoSavings.total.add(additionalCredit);

        compositePrice = compositePrice.minus(additionalCredit);

        // Depending on tax config, add the credit into the total. This is to make the subtotal on the checkout ticket
        // look more like the menu.
        if (discountTaxOrder === 'taxesFirst' || Big(detail.basePriceMult, `Base Price Mult`).eq(1)) {
          credit = credit.plus(additionalCredit);
        } else {
          productTotal = productTotal.minus(additionalCredit);
        }

        if (receiptDetail && additionalCredit.gt(0)) {
          receiptDetail.addDiscount({
            type: `bogo`,
            value: additionalCredit.times(100),
            id: bogoReward.specialId,
            name: bogoReward.specialName,
            additionalAttributes: {
              satisfiedBy: _.values(bogoReward.satisfiedBy),
            },
          });
        }
      }
    });

    if (!stowDiscounts) {
      detail.bogoSavings = bogoSavings;
    }
  }

  return { bogoRewardsApplied, compositePrice, credit, productTotal, stowedDiscounts };
};

export default calculateBogoDiscounts;
